import React from 'react';
import { motion } from 'framer-motion';
/**
 * TableOfContentsVisual — STU-INHOUDSOPGAVE visual aid
 *
 * Renders a realistic table of contents: hoofdstuknummers, titels, gestippelde
 * leaders en paginanummers in monospace. Hoofdstukken kunnen sub-secties
 * hebben (indentation). Highlight-modus per rij voor support-feedback.
 *
 * Data shape:
 * {
 *   title?: string                      // bv. "Inhoudsopgave" — default
 *   entries: Array<{
 *     number?: string                   // bv. "1", "2.1", "I"
 *     title: string
 *     page: number | string
 *     indent?: number                   // 0 = hoofdstuk, 1 = sub, 2 = sub-sub
 *     highlight?: boolean
 *   }>
 *   bookTitle?: string                  // optionele titel bovenaan
 *   label?: string
 *   mode?: 'stimulus' | 'support'
 * }
 */
interface TocEntry {
  number?: string;
  title: string;
  page: number | string;
  indent?: number;
  highlight?: boolean;
}
interface TableOfContentsVisualProps {
  title?: string;
  bookTitle?: string;
  entries?: TocEntry[];
  label?: string;
  mode?: 'stimulus' | 'support';
  numberStyle?: 'decimal' | 'roman' | 'none';
}
export function TableOfContentsVisual({
  title,
  bookTitle,
  entries = [],
  label,
  mode = 'stimulus',
  numberStyle = 'decimal'
}: TableOfContentsVisualProps) {
  const safeEntries = Array.isArray(entries) ? entries : [];
  if (safeEntries.length === 0) {
    return <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500" data-id="element-13226">
        Inhoudsopgave kan niet worden getoond (geen items).
      </div>;
  }
  const isStimulus = mode === 'stimulus';
  return <figure className={`flex flex-col gap-3 rounded-xl border-2 ${isStimulus ? 'border-amber-200 bg-amber-50/30' : 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm'} p-5 max-w-md mx-auto`} aria-label={label ?? 'Inhoudsopgave'} data-id="element-13227">
      {/* Book "cover" header */}
      {bookTitle && <div className="flex items-center gap-2 border-b-2 border-amber-300 pb-2 mb-1" data-id="element-13228">
          <span aria-hidden="true" className="text-lg" data-id="element-13229">
            📖
          </span>
          <span className="text-xs font-bold uppercase tracking-wide text-amber-900" data-id="element-13230">
            {bookTitle}
          </span>
        </div>}

      <figcaption className="text-base font-bold text-amber-900 text-center" data-id="element-13231">
        {title ?? 'Inhoudsopgave'}
      </figcaption>

      <ol className="flex flex-col gap-1.5 font-serif" data-id="element-13232">
        {safeEntries.map((entry, i) => {
        const indent = entry.indent ?? 0;
        const isHi = !!entry.highlight;
        return <motion.li key={i} initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: i * 0.05,
          duration: 0.25
        }} className={`flex items-baseline gap-2 rounded px-2 py-1 text-sm ${isHi ? 'bg-amber-200 ring-2 ring-amber-500 font-semibold text-amber-950' : 'text-slate-800'}`} style={{
          paddingLeft: `${0.5 + indent * 1.25}rem`
        }} data-id="element-13233">
              {entry.number && numberStyle !== 'none' && <span className={`font-mono text-xs font-bold ${isHi ? 'text-amber-900' : 'text-amber-700'} shrink-0`} data-id="element-13234">
                  {indent === 0 && numberStyle === 'decimal' ? `Hoofdstuk ${entry.number}` : entry.number}
                </span>}
              <span className={`${entry.number ? '' : 'font-semibold'} ${indent > 0 ? 'text-slate-600 text-[13px]' : ''}`} data-id="element-13235">
                {entry.title}
              </span>
              <span className="flex-1 border-b border-dotted border-slate-400 mb-1 mx-1" aria-hidden="true" data-id="element-13236" />
              <span className={`font-mono font-semibold tabular-nums ${isHi ? 'text-amber-900' : 'text-slate-700'}`} data-id="element-13237">
                {entry.page}
              </span>
            </motion.li>;
      })}
      </ol>
    </figure>;
}
export default TableOfContentsVisual;